import { Player } from '@lottiefiles/react-lottie-player';
import { useRouter } from 'next/router';
import React, {
  FunctionComponent,
  memo,
  useEffect,
  useRef,
  useState,
} from 'react';
import { InView } from 'react-intersection-observer';

import mapImage from 'assets/images/map.jpg';
import { useCopyStore, useGlobalStore } from 'store';
import { ReactComponent as CursorIcon } from 'svgs/cursor.svg';
import { ReactComponent as FactoriesIcon } from 'svgs/factoriesSignature.svg';
import { ReactComponent as FoodIcon } from 'svgs/foodSignature.svg';
import { ReactComponent as HandIcon } from 'svgs/hand.svg';
import highlight from 'svgs/highlight';
import { ReactComponent as HousingIcon } from 'svgs/housingSignature.svg';
import { ReactComponent as MilitaryIcon } from 'svgs/militarySignature.svg';
import { ReactComponent as NorthArrowIcon } from 'svgs/northArrow.svg';
import { ReactComponent as RoadsIcon } from 'svgs/roadsSignature.svg';
import { ReactComponent as ArrowLeftIcon } from 'svgs/smallArrowLeft.svg';
import { ReactComponent as ArrowRightIcon } from 'svgs/smallArrowRight.svg';
import { ReactComponent as WaterIcon } from 'svgs/waterSignature.svg';
import Lottie from 'u9/components/Lottie/Lottie';
import gtm, { actions, categories } from 'u9/utils/gtm';
import { THEMES } from 'utils/config';
import { ROUTES } from 'utils/routes';
import { pageMotionProps } from 'utils/styles/animations';

import { MarkerProps, markers } from './Map.utils';

import * as Styled from './Map.styles';

const MARKER_ICON = {
  [THEMES.FOOD]: <FoodIcon />,
  [THEMES.HOUSING]: <HousingIcon />,
  [THEMES.FACTORIES]: <FactoriesIcon />,
  [THEMES.ROADS]: <RoadsIcon />,
  [THEMES.WATER]: <WaterIcon />,
  [THEMES.MILITARY]: <MilitaryIcon />,
};

const Map: FunctionComponent = () => {
  const {
    copy: { global, themes, accessibility },
  } = useCopyStore();
  const {
    currentTheme,
    isSmallDevice,
    isLandingVisible,
    setCurrentTheme,
    isCardExpanded,
    setLandingVisible,
  } = useGlobalStore();
  const router = useRouter();
  const lottiePlayerRef = useRef<Player>(null);
  const constraintsRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [markersInView, setMarkersInView] = useState<boolean[]>(
    Array(6).fill(false)
  );
  const [sliderLabels, setSliderLabels] = useState<{
    left: number;
    right: number;
  }>({
    left: 0,
    right: 0,
  });
  const [windowHeightSize, setWindowHeightSize] = useState<number>(null);
  const [sliderProgress, setSliderProgress] = useState<number>(0);
  const [activeMarker, setActiveMarker] = useState<string | null>(null);

  useEffect(() => {
    setActiveMarker(null);
    if (!currentTheme) {
      window.document.title = global?.common?.mapLabel;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router]);

  useEffect(() => {
    setWindowHeightSize(window.innerHeight);
  }, []);

  useEffect(() => {
    const handleWindowResize = () => {
      setWindowHeightSize(window.innerHeight);
    };
    window.addEventListener('resize', handleWindowResize);
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  useEffect(() => {
    if (carouselRef?.current && isSmallDevice) {
      carouselRef?.current.addEventListener('scroll', handleScroll);
    }
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      carouselRef?.current?.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const lastVisible = markersInView.lastIndexOf(true);
    const invisibleElements = markersInView.reduce((a, e, i) => {
      if (lastVisible === -1) {
        a[0] = 6;
      } else if (!e && i < lastVisible) {
        a[0] = a[0] ? a[0] + 1 : 1;
      } else if (!e && i > lastVisible) {
        a[1] = a[1] ? a[1] + 1 : 1;
      }
      return a;
    }, []);
    setSliderLabels({
      left: invisibleElements[0],
      right: invisibleElements[1],
    });
  }, [markersInView]);

  const handleScroll = (e: any) => {
    const clientWidth = e.target.clientWidth;
    const containerWidth = e.target.scrollWidth - clientWidth;
    const stepRange = containerWidth / clientWidth;
    const offset = e.target.scrollLeft;
    const progress = ((offset / stepRange) * 100) / clientWidth;
    setSliderProgress(progress);
  };

  const handleMarkerClick = (theme: string | null) => {
    setActiveMarker(theme);

    setTimeout(() => {
      setCurrentTheme(theme);
      router.push({
        pathname: ROUTES.THEMES,
        query: {
          id: theme.toLowerCase(),
        },
      });
    }, 1000);
    gtm.trackEvent(categories.map, actions.click, theme);
  };

  const handleMarkerVisibility = (index: number, inView: boolean) => {
    const markers = [...markersInView];
    markers[index] = inView;
    setMarkersInView(markers);
  };

  const backToLanding = () => {
    setCurrentTheme(null);
    setLandingVisible(true);
  };

  if (!themes) return null;

  return (
    <Styled.Container {...pageMotionProps} ref={constraintsRef} role="main">
      <Styled.NorthArrow
        initial={{ opacity: 0 }}
        animate={
          router?.pathname === ROUTES.HOME
            ? { opacity: 1 }
            : isCardExpanded
            ? { opacity: 0 }
            : { opacity: 1 }
        }
        transition={{ duration: 0.6 }}
      >
        <NorthArrowIcon />
      </Styled.NorthArrow>
      {!currentTheme ? (
        <Styled.MapLabel
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          onClick={backToLanding}
        >
          <h1
            dangerouslySetInnerHTML={{ __html: global?.common?.mapLabel }}
          ></h1>
        </Styled.MapLabel>
      ) : null}
      <Styled.Wrapper>
        <div ref={carouselRef}>
          <Styled.MapContent height={windowHeightSize}>
            <Styled.MapImage
              tabIndex={0}
              src={mapImage}
              alt={
                currentTheme
                  ? accessibility?.map?.themes?.[currentTheme]?.map
                  : global?.common?.map
              }
              aria-live="polite"
            />
            {!currentTheme ? (
              <Styled.MarkersWrapper>
                {markers.map(({ theme, x, y, content }: MarkerProps, index) => (
                  <div key={index}>
                    <Styled.Marker
                      x={x}
                      y={y}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.6, delay: (index + 1) * 0.2 }}
                    >
                      <Styled.Line theme={theme} lineWidth={content?.lineWidth}>
                        <Styled.Circle onClick={() => handleMarkerClick(theme)}>
                          <InView
                            as="div"
                            onChange={inView =>
                              handleMarkerVisibility(index, inView)
                            }
                            threshold={0}
                          >
                            <Styled.InnerCircle>
                              {highlight(index)}
                            </Styled.InnerCircle>
                            <Styled.IconWrapper
                              theme={theme}
                              active={theme === activeMarker}
                            >
                              <div>{MARKER_ICON?.[theme]}</div>
                            </Styled.IconWrapper>
                            <Styled.TitleWrapper theme={theme}>
                              <span
                                aria-label={
                                  accessibility?.map?.themes?.[theme]
                                    ?.buttonInscription
                                }
                              >
                                {themes?.[theme]?.title}
                              </span>
                            </Styled.TitleWrapper>
                          </InView>
                        </Styled.Circle>
                      </Styled.Line>
                    </Styled.Marker>
                  </div>
                ))}
              </Styled.MarkersWrapper>
            ) : (
              <Styled.Overlay theme={currentTheme}>
                <Lottie
                  ref={lottiePlayerRef}
                  animation={
                    `${
                      process.env.LOCAL_EXPORT ? process.env.BASE_PATH : ''
                    }/lottie/map/${
                      currentTheme === 'FOOD'
                        ? 'housing'
                        : currentTheme?.toLowerCase()
                    }.json` as any
                  }
                  autoPlay={true}
                  loop={false}
                  useCanvas={false}
                  keepLastFrame={true}
                />
              </Styled.Overlay>
            )}
          </Styled.MapContent>
        </div>
        {router?.pathname === ROUTES.HOME ? (
          <Styled.RangeSliderContainer>
            <Styled.RangeSliderLabels>
              <Styled.Label
                initial={{ opacity: 0 }}
                animate={sliderLabels?.left ? { opacity: 1 } : { opacity: 0 }}
                transition={{ duration: sliderLabels?.left ? 0.3 : 0 }}
                aria-hidden="true"
              >
                <ArrowLeftIcon />
                <span>{sliderLabels?.left}</span>
              </Styled.Label>
              <Styled.Label
                initial={{ opacity: 0 }}
                animate={sliderLabels?.right ? { opacity: 1 } : { opacity: 0 }}
                transition={{ duration: sliderLabels?.right ? 0.3 : 0 }}
                aria-hidden="true"
              >
                <span>{sliderLabels?.right}</span>
                <ArrowRightIcon />
              </Styled.Label>
            </Styled.RangeSliderLabels>
            <Styled.SliderWrapper>
              <Styled.Slider>
                <Styled.Knob style={{ x: `${sliderProgress * 3.3}%` }} />
              </Styled.Slider>
            </Styled.SliderWrapper>
          </Styled.RangeSliderContainer>
        ) : null}
      </Styled.Wrapper>
      <Styled.Mask />
      {isSmallDevice && router?.pathname === ROUTES.HOME ? (
        <Styled.MobileHint
          initial={{ opacity: 0 }}
          variants={{
            invisible: {
              opacity: 0,
              transition: {
                duration: 0.6,
                delay: 2,
              },
            },
            visible: {
              opacity: 1,
              transition: {
                duration: 0.6,
                delay: 2,
              },
              transitionEnd: {
                opacity: 0,
                transitionDelay: '6s',
                transitionDuration: '0.6s',
                visibility: 'hidden',
              },
            },
            hidden: {
              display: 'none',
              transition: {
                delay: 7,
              },
            },
          }}
          animate={isLandingVisible ? ['invisible'] : ['visible', 'hidden']}
          aria-hidden="true"
        >
          <HandIcon />
          <p
            dangerouslySetInnerHTML={{ __html: global?.common?.mapHintMobile }}
          ></p>
        </Styled.MobileHint>
      ) : null}
      {!isSmallDevice && router?.pathname === ROUTES.HOME ? (
        <Styled.Hint
          initial={{ opacity: 0, y: 50 }}
          animate={
            isLandingVisible
              ? { opacity: 0, y: 50 }
              : {
                  opacity: 1,
                  y: 0,
                  transitionEnd: {
                    opacity: 0,
                    transitionDelay: '4s',
                    transitionDuration: '0.6s',
                  },
                }
          }
          transition={{ duration: 0.6, delay: 2 }}
          aria-hidden="true"
        >
          <CursorIcon />
          <p dangerouslySetInnerHTML={{ __html: global?.common?.mapHint }}></p>
        </Styled.Hint>
      ) : null}
    </Styled.Container>
  );
};

export default memo(Map);
